import type { SectionValue, ThingsDoneWellSection as ThingsDoneWellData } from "../../../../shared/analysis-types";
import { SectionHeader } from "./section-header";
import { NarrativeCard, NarrativeCardLede } from "./narrative-card";
import { SourceQuoteBlock } from "./source-quote-block";
import { SkillRefChip } from "./skill-ref-chip";
import { SectionErrorFallback } from "./section-error-fallback";
import { CopyFormattedButton } from "@/components/copy-formatted-button";
import {
  formatThingDoneWellCopy,
  formatThingsDoneWellCopy,
} from "@/lib/analysis-copy-format";

interface ThingsDoneWellSectionProps {
  data: SectionValue<ThingsDoneWellData>;
  className?: string;
}

export function ThingsDoneWellSection({
  data,
  className,
}: ThingsDoneWellSectionProps) {
  if ("error" in data) {
    return (
      <div className={className}>
        <SectionHeader title="Things Done Well" />
        <div className="mt-4">
          <SectionErrorFallback sectionName="things_done_well" />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <SectionHeader
        title="Things Done Well"
        count={data.observations.length}
        action={
          <CopyFormattedButton format={formatThingsDoneWellCopy(data)}>
            Copy section
          </CopyFormattedButton>
        }
      />
      <div className="mt-4 flex flex-col gap-6">
        {data.observations.map((obs, i) => (
          <NarrativeCard key={i} className="group relative pt-14 sm:pt-5 sm:pr-32">
            <CopyFormattedButton
              format={formatThingDoneWellCopy(obs)}
              className="absolute right-5 top-5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
            >
              Copy item
            </CopyFormattedButton>
            <NarrativeCardLede>{obs.lede}</NarrativeCardLede>

            {obs.rd_quote && (
              <SourceQuoteBlock quote={obs.rd_quote} label="SOURCE QUOTE" />
            )}

            <p className="text-sm leading-relaxed text-foreground">
              {obs.body}
            </p>

            {obs.skill_refs.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {obs.skill_refs.map((ref) => (
                  <SkillRefChip key={ref} label={ref} />
                ))}
              </div>
            )}
          </NarrativeCard>
        ))}
      </div>
    </div>
  );
}
